import { css, Theme } from '@emotion/react';

const buttonBase = (theme: Theme) => css`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: ${theme.scale.space2xs};
	min-height: ${theme.ui.form.controlHeight};
	padding: 0 2rem;
	border-radius: ${theme.ui.form.controlRadius};
	border: 1px solid transparent;
	font-size: ${theme.fontSize.sizeMd};
	font-weight: ${theme.fontWeight.weightSemi};
	line-height: 1.2;
	letter-spacing: ${theme.ui.letterSpacing.body};
	white-space: nowrap;
	cursor: pointer;
	transition:
		background-color ${theme.effects.transitionFast},
		border-color ${theme.effects.transitionFast},
		color ${theme.effects.transitionFast},
		box-shadow ${theme.effects.transitionFast},
		transform ${theme.effects.transitionFast};

	&:focus-visible {
		outline: none;
		box-shadow: 0 0 0 3px ${theme.ui.interactive.focusRing};
	}

	&:disabled {
		background-color: ${theme.ui.surface.muted} !important;
		border-color: ${theme.ui.border.default} !important;
		color: ${theme.ui.text.muted} !important;
		box-shadow: none !important;
		transform: none !important;
		cursor: not-allowed;
	}
`;

export const primaryButtonStyles = (theme: Theme, fullWidth?: boolean) => css`
	${buttonBase(theme)};
	width: ${fullWidth ? '100%' : 'auto'};
	background-color: ${theme.ui.interactive.primary};
	color: ${theme.ui.text.inverse};
	box-shadow: ${theme.effects.shadowSm};

	&:hover:not(:disabled) {
		background-color: ${theme.ui.interactive.primaryHover};
		box-shadow: ${theme.effects.shadowMd};
	}

	&:active:not(:disabled) {
		transform: translateY(1px);
	}
`;

export const inlineButtonStyles = (theme: Theme) => css`
	${buttonBase(theme)};
	flex-shrink: 0;
	min-width: 9.6rem;
	height: ${theme.ui.form.controlHeight};
	padding: 0 1.6rem;
	background-color: ${theme.ui.text.primary};
	color: ${theme.ui.text.inverse};

	&:hover:not(:disabled) {
		background-color: ${theme.colors.grayDarker};
	}
`;

export const outlineButtonStyles = (theme: Theme, fullWidth?: boolean) => css`
	${buttonBase(theme)};
	width: ${fullWidth ? '100%' : 'auto'};
	background-color: ${theme.ui.surface.page};
	border-color: ${theme.ui.interactive.primary};
	color: ${theme.ui.interactive.primary};

	&:hover:not(:disabled) {
		background-color: ${theme.ui.interactive.primaryMuted};
	}
`;

export const textButtonStyles = (theme: Theme) => css`
	${buttonBase(theme)};
	min-height: auto;
	padding: 0.4rem 0.6rem;
	background: transparent;
	border-color: transparent;
	color: ${theme.ui.text.muted};
	font-size: ${theme.fontSize.sizeSm};
	font-weight: ${theme.fontWeight.weightRegular};

	&:hover:not(:disabled) {
		color: ${theme.ui.interactive.primary};
		background-color: transparent;
	}
`;
